import { useMemo } from 'react';
import { useTheme as muiUseTheme } from '@mui/material/styles';
import { type Theme as muiTheme } from '@mui/material/styles';
import { getLightThemeOptions, getLightTheme } from './themes/light';
import { getDarkTheme } from './themes/dark';
import { ThemeNames } from './themeProvider';
import { useBrandingOverrides } from './brandingContext';
import { themeDeepMerge } from './utils/themeDeepMerge';
import { paletteTokens } from './generated/paletteVariables';

export type LightThemeOptions = ReturnType<typeof getLightThemeOptions>;

type ThemePalette = muiTheme['palette'] &
  Omit<LightThemeOptions['palette'], 'mode'> & { mode: 'light' | 'dark' };

export type Theme = Omit<muiTheme & LightThemeOptions, 'palette'> & {
  palette: ThemePalette;
};

const themeGetters = {
  light: getLightTheme,
  dark: getDarkTheme,
};

/**
 * Unbranded base themes are pure functions of their palette argument, so the
 * no-override result is stable for a given theme name. Build each variant
 * lazily, once, and reuse it — instead of calling `createTheme()` on every
 * render or whenever unrelated context changes.
 */
const baseThemeCache: Partial<Record<ThemeNames, Theme>> = {};

const getBaseTheme = (themeName: ThemeNames): Theme => {
  baseThemeCache[themeName] ??= themeGetters[themeName]() as Theme;
  return baseThemeCache[themeName] as Theme;
};

/**
 * Hook to access the current theme or a specific theme variant.
 *
 * @param themeName - Optional. If provided, returns the branded version of that
 *   theme variant. If omitted, returns the current theme from MUI context.
 *
 * When a themeName is specified, the hook will:
 * 1. Get branding overrides from BrandingContext (if available)
 * 2. Apply those overrides to the requested theme variant
 * 3. Return the branded theme
 *
 * The by-name path intentionally does not depend on the MUI context theme: a
 * requested variant is derived from its own tokens (plus branding), so context
 * churn must not rebuild it.
 */
export function useTheme(themeName?: ThemeNames): Theme {
  const contextTheme = muiUseTheme<Theme>();
  const branding = useBrandingOverrides();

  const namedTheme = useMemo(() => {
    if (themeName === undefined) {
      return undefined;
    }

    const overridePalette = branding?.overridePalette;
    const overrideTheme = branding?.overrideTheme;

    if (!overridePalette && !overrideTheme) {
      return getBaseTheme(themeName);
    }

    const palette = overridePalette
      ? (themeDeepMerge(paletteTokens, overridePalette) as typeof paletteTokens)
      : undefined;

    const baseTheme = themeGetters[themeName](palette);

    if (!overrideTheme) {
      return baseTheme as Theme;
    }

    return themeDeepMerge(
      baseTheme as unknown as Record<string, unknown>,
      overrideTheme,
    ) as Theme;
  }, [themeName, branding]);

  return themeName === undefined ? contextTheme : (namedTheme as Theme);
}
